import './LegalEditor.css';
import { APP_LABELS, LegalApp, stateTagColor, typeLabel } from './legalConstants';
import {
  Alert,
  Box,
  Button,
  ConfirmationModal,
  DateInput,
  Modal,
  Tag,
  Text,
} from '@tocktix/pantry';
import { EditorContent, useEditor } from '@tiptap/react';
import {
  LegalPageDraftsDocument,
  LegalPageFieldsFragment,
  LegalPageHistoryDocument,
  LegalPageType,
  useCreateLegalPageDraftMutation,
  useDiscardLegalPageDraftMutation,
  useLegalPageDraftsQuery,
  useLegalPageHistoryQuery,
  useLegalPageReviewEventsLazyQuery,
  useLegalPageReviewEventsQuery,
  usePublishLegalPageMutation,
  useRestoreAndPublishLegalPageVersionMutation,
  useRestoreLegalPageVersionMutation,
  useSubmitLegalPageForReviewMutation,
  useUpdateLegalPageDraftMutation,
  useUpdateLegalPageEffectiveDateMutation,
  useWithdrawLegalPageReviewMutation,
} from 'generated/graphql';
import { legalEditorExtensions } from './legalEditorExtensions';
import { legalHtmlHasChanges } from 'utils/legalHtmlDiff';
import { renderLegalContentHtml } from './renderLegalContentHtml';
import { sanitizeLegalHtml } from 'utils/legalHtmlSanitizer';
import { useActions } from 'utils/connect';
import { useCallback, useEffect, useRef, useState } from 'react';
import EditorContentMenuBar from 'components/EditorContentMenuBar';

interface Props {
  app: LegalApp;
  type: string;
  /** Required for every app; both Tock and JCE pages are localized. */
  locale: string;
  /** Pre-select this draft on mount (e.g. deep-linked from an approved review). */
  initialDraftId?: number | null;
  onSubmitForReviewSuccess: () => void;
  onPublishSuccess: () => void;
}

const PANE_HEIGHT = 480;

const CENTRAL_TIME_ZONE = 'America/Chicago';

const toErrorMessage = (error: unknown): string =>
  error instanceof Error ? error.message : 'Something went wrong.';

/**
 * Renders a stored ISO instant as the CST calendar date (`YYYY-MM-DD`) for an
 * `<input type="date">`, so authors see the same day the consumer will.
 */
const toCentralDateInputValue = (iso?: string | null): string => {
  if (!iso) {
    return '';
  }
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return '';
  }
  // en-CA formats as YYYY-MM-DD, which is what <input type="date"> expects.
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: CENTRAL_TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(date);
};

/**
 * Renders a stored ISO instant as a human-readable CST calendar date, so an
 * effective date shows the same day regardless of the viewer's browser
 * timezone (matching the CST-based `<input type="date">` value above).
 */
const formatCentralDate = (iso?: string | null): string => {
  if (!iso) {
    return '\u2014';
  }
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return '\u2014';
  }
  return new Intl.DateTimeFormat('en-US', {
    timeZone: CENTRAL_TIME_ZONE,
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
  }).format(date);
};

/**
 * Strips empty paragraphs/list items left behind by rich-text paste so legal
 * content does not accumulate blank blocks. Mirrors the v5 editor behavior.
 */
const cleanPastedLegalHtml = (html: string): string => {
  if (!html) {
    return html;
  }

  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');
  const hasMeaningfulContent = (element: Element): boolean => {
    if (element.querySelector('img,video,iframe,svg,table,ul,ol,blockquote,pre,hr')) {
      return true;
    }
    const text = (element.textContent ?? '').replace(/\u00a0/g, ' ').trim();
    return text.length > 0;
  };

  doc.querySelectorAll('li').forEach((item) => {
    if (!hasMeaningfulContent(item)) {
      item.remove();
    }
  });

  doc.querySelectorAll('p').forEach((paragraph) => {
    if (!hasMeaningfulContent(paragraph)) {
      paragraph.remove();
    }
  });

  return doc.body.innerHTML;
};

const LegalEditor: React.FunctionComponent<Props> = ({
  app,
  type,
  locale,
  initialDraftId,
  onSubmitForReviewSuccess,
  onPublishSuccess,
}) => {
  const { actions } = useActions();

  const [activeDraftId, setActiveDraftId] = useState<number | null>(initialDraftId ?? null);
  const [historyEventsId, setHistoryEventsId] = useState<number | null>(null);
  const [publishConfirmOpen, setPublishConfirmOpen] = useState(false);
  const [withdrawConfirmOpen, setWithdrawConfirmOpen] = useState(false);
  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false);
  // Version id pending a confirmed "Restore & publish" (skip-review) action.
  const [restoreAndPublishVersionId, setRestoreAndPublishVersionId] = useState<number | null>(null);
  const [previewHtml, setPreviewHtml] = useState('');
  // Per-page in-progress edits to the effective date, keyed by legal page id.
  const [effectiveDateEdits, setEffectiveDateEdits] = useState<Record<string, string>>({});

  const editorScrollRef = useRef<HTMLDivElement | null>(null);
  const previewScrollRef = useRef<HTMLDivElement | null>(null);
  const isSyncingScrollRef = useRef(false);

  const typeEnum = type as LegalPageType;
  // The request context is identical for every read/write in this editor.
  const queryVariables = { type: typeEnum, app, locale };
  const draftsRefetch = { query: LegalPageDraftsDocument, variables: queryVariables };
  const historyRefetch = { query: LegalPageHistoryDocument, variables: queryVariables };

  const draftsQuery = useLegalPageDraftsQuery({
    variables: queryVariables,
    onError: () => actions.toast.showError("Couldn't load drafts."),
  });
  const historyQuery = useLegalPageHistoryQuery({
    variables: queryVariables,
    onError: () => actions.toast.showError("Couldn't load published history."),
  });

  const drafts: LegalPageFieldsFragment[] = draftsQuery.data?.legalPageDrafts ?? [];
  const history: LegalPageFieldsFragment[] = historyQuery.data?.legalPageHistory ?? [];
  const activeDraft = drafts.find((draft) => draft.id === activeDraftId) ?? null;

  // Fetch review events for the active draft so we can show a rejection banner.
  const reviewEventsQuery = useLegalPageReviewEventsQuery({
    variables: { legalPageId: activeDraftId ?? 0 },
    skip: activeDraftId === null,
    onError: () => actions.toast.showError("Couldn't load review history."),
  });
  const reviewEvents =
    activeDraftId === null ? [] : (reviewEventsQuery.data?.legalPageReviewEvents ?? []);

  // Lazy query backing the per-draft "Review history" modal.
  const [loadHistoryEvents, historyEventsQuery] = useLegalPageReviewEventsLazyQuery({
    fetchPolicy: 'network-only',
    onError: () => actions.toast.showError("Couldn't load review history."),
  });
  const historyEvents =
    historyEventsQuery.variables?.legalPageId === historyEventsId
      ? (historyEventsQuery.data?.legalPageReviewEvents ?? [])
      : [];
  const historyEventsLoading = historyEventsQuery.loading;

  const [createDraft] = useCreateLegalPageDraftMutation();
  const [updateDraft] = useUpdateLegalPageDraftMutation();
  const [submitForReview] = useSubmitLegalPageForReviewMutation();
  const [publishLegalPage] = usePublishLegalPageMutation();
  const [restoreVersion] = useRestoreLegalPageVersionMutation();
  const [restoreAndPublish] = useRestoreAndPublishLegalPageVersionMutation();
  const [updateEffectiveDate] = useUpdateLegalPageEffectiveDateMutation();
  const [withdrawReview] = useWithdrawLegalPageReviewMutation();
  const [discardDraft] = useDiscardLegalPageDraftMutation();

  const latestRejectionEvent =
    [...reviewEvents]
      .filter((event) => event.eventType === 'REJECTED')
      .sort((a, b) => (b.createdAt ?? '').localeCompare(a.createdAt ?? ''))[0] ?? null;
  const rejected = activeDraft?.state === 'DRAFT' && Boolean(latestRejectionEvent);

  // For an APPROVED draft awaiting publish, compare against the row currently live in `history`.
  const currentPublished = history.find((version) => version.state === 'PUBLISHED') ?? null;
  const currentPublishedHtml = renderLegalContentHtml(currentPublished?.content) ?? '';
  const activeDraftHtml = renderLegalContentHtml(activeDraft?.content) ?? '';
  const isApprovedActiveDraft = activeDraft?.state === 'APPROVED';
  // Q2: the live page changed since approval, so the server will reject the publish. Disable the
  // button and explain, rather than letting the user hit a confusing failure. Null-safe compare so
  // "nothing published then and now" is not treated as stale.
  const staleApproved =
    isApprovedActiveDraft &&
    (currentPublished?.id ?? null) !== (activeDraft?.approvedAgainstVersionId ?? null);
  // Q4: this approved draft is byte-for-byte identical to the live page, so publishing is a no-op
  // that only re-archives the live row and resets its effective date. Non-blocking — just warn.
  const isNoOpPublish =
    isApprovedActiveDraft &&
    currentPublishedHtml.trim().length > 0 &&
    !legalHtmlHasChanges(currentPublishedHtml, activeDraftHtml);

  const editor = useEditor({
    extensions: legalEditorExtensions(),
    content: '',
    onUpdate({ editor: instance }) {
      setPreviewHtml(instance.getHTML());
    },
    editorProps: {
      transformPastedHTML: cleanPastedLegalHtml,
    },
  });

  // Sync editor content when the active draft changes (load or clear).
  useEffect(() => {
    if (!editor) {
      return;
    }
    if (activeDraftId === null) {
      editor.commands.clearContent();
      setPreviewHtml('');
      return;
    }
    if (activeDraft?.content) {
      try {
        editor.commands.setContent(JSON.parse(activeDraft.content));
      } catch {
        // Legacy rows can carry non-JSON `content`; TipTap accepts an HTML
        // string, so clear rather than throwing on a parse failure.
        editor.commands.setContent('');
      }
      setPreviewHtml(editor.getHTML());
    }
  }, [activeDraftId, activeDraft?.content, editor]);

  // Only DRAFT pages (and the new-draft state) are editable; everything else is read-only.
  useEffect(() => {
    if (!editor) {
      return;
    }
    editor.setEditable(activeDraftId === null || activeDraft?.state === 'DRAFT');
  }, [editor, activeDraftId, activeDraft?.state]);

  const syncPaneScroll = useCallback((source: HTMLDivElement, target: HTMLDivElement) => {
    if (isSyncingScrollRef.current) {
      return;
    }
    const sourceMax = source.scrollHeight - source.clientHeight;
    const targetMax = target.scrollHeight - target.clientHeight;
    const scrollRatio = sourceMax > 0 ? source.scrollTop / sourceMax : 0;

    isSyncingScrollRef.current = true;
    target.scrollTop = targetMax > 0 ? scrollRatio * targetMax : 0;
    requestAnimationFrame(() => {
      isSyncingScrollRef.current = false;
    });
  }, []);

  const handleEditorPaneScroll = useCallback(() => {
    if (!editorScrollRef.current || !previewScrollRef.current) {
      return;
    }
    syncPaneScroll(editorScrollRef.current, previewScrollRef.current);
  }, [syncPaneScroll]);

  const handlePreviewPaneScroll = useCallback(() => {
    if (!editorScrollRef.current || !previewScrollRef.current) {
      return;
    }
    syncPaneScroll(previewScrollRef.current, editorScrollRef.current);
  }, [syncPaneScroll]);

  const handleSave = useCallback(async () => {
    if (!editor) {
      return;
    }
    const content = JSON.stringify(editor.getJSON());

    try {
      if (activeDraftId !== null) {
        await updateDraft({
          variables: { id: activeDraftId, content },
          refetchQueries: [draftsRefetch],
          awaitRefetchQueries: true,
        });
        actions.toast.showInfo('Draft saved.');
        return;
      }

      const res = await createDraft({
        variables: { type: typeEnum, app, locale, content },
        refetchQueries: [draftsRefetch],
        awaitRefetchQueries: true,
      });
      const page = res.data?.createLegalPageDraft;
      if (page) {
        setActiveDraftId(page.id);
        actions.toast.showInfo('Draft saved.');
      }
    } catch (error) {
      actions.toast.showError(toErrorMessage(error));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editor, activeDraftId, actions, type, app, locale]);

  const handleSubmitForReview = useCallback(async () => {
    if (!editor) {
      return;
    }
    let draftId = activeDraftId;
    const content = JSON.stringify(editor.getJSON());

    try {
      if (draftId === null) {
        // If nothing has been saved yet, create the draft first.
        const created = await createDraft({
          variables: { type: typeEnum, app, locale, content },
          refetchQueries: [draftsRefetch],
          awaitRefetchQueries: true,
        });
        const page = created.data?.createLegalPageDraft;
        if (!page) {
          return;
        }
        draftId = page.id;
        setActiveDraftId(draftId);
      } else {
        // Persist the current editor content so the saved draft (which may have
        // been created earlier via Edit/restore) reflects what is being reviewed.
        await updateDraft({ variables: { id: draftId, content } });
      }

      await submitForReview({
        variables: { id: draftId },
        refetchQueries: [draftsRefetch, historyRefetch],
        awaitRefetchQueries: true,
      });
      setActiveDraftId(null);
      onSubmitForReviewSuccess();
    } catch (error) {
      actions.toast.showError(toErrorMessage(error));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editor, activeDraftId, onSubmitForReviewSuccess, actions, type, app, locale]);

  const handlePublishApproved = useCallback(async () => {
    if (activeDraftId === null) {
      return;
    }
    try {
      await publishLegalPage({
        variables: { id: activeDraftId },
        refetchQueries: [draftsRefetch, historyRefetch],
        awaitRefetchQueries: true,
      });
      setPublishConfirmOpen(false);
      setActiveDraftId(null);
      onPublishSuccess();
    } catch (error) {
      setPublishConfirmOpen(false);
      actions.toast.showError(toErrorMessage(error));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeDraftId, onPublishSuccess, actions]);

  const handleWithdrawReview = useCallback(async () => {
    if (activeDraftId === null) {
      return;
    }
    try {
      await withdrawReview({
        variables: { id: activeDraftId },
        refetchQueries: [draftsRefetch, historyRefetch],
        awaitRefetchQueries: true,
      });
      setWithdrawConfirmOpen(false);
      setActiveDraftId(null);
      actions.toast.showInfo('Review withdrawn — you can edit and resubmit.');
    } catch (error) {
      setWithdrawConfirmOpen(false);
      actions.toast.showError(toErrorMessage(error));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeDraftId, actions]);

  const handleDiscardDraft = useCallback(async () => {
    if (activeDraftId === null) {
      return;
    }
    try {
      await discardDraft({
        variables: { id: activeDraftId },
        refetchQueries: [draftsRefetch],
        awaitRefetchQueries: true,
      });
      setDiscardConfirmOpen(false);
      setActiveDraftId(null);
      actions.toast.showInfo('Draft discarded.');
    } catch (error) {
      setDiscardConfirmOpen(false);
      actions.toast.showError(toErrorMessage(error));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeDraftId, actions]);

  const handleRestore = useCallback(
    async (versionId: number) => {
      try {
        const res = await restoreVersion({
          variables: { versionId },
          refetchQueries: [draftsRefetch],
          awaitRefetchQueries: true,
        });
        const page = res.data?.restoreLegalPageVersion;
        if (page) {
          setActiveDraftId(page.id);
        }
      } catch (error) {
        actions.toast.showError(toErrorMessage(error));
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [actions]
  );

  const handleRestoreAndPublish = useCallback(async () => {
    if (restoreAndPublishVersionId === null) {
      return;
    }
    try {
      await restoreAndPublish({
        variables: { versionId: restoreAndPublishVersionId },
        refetchQueries: [draftsRefetch, historyRefetch],
        awaitRefetchQueries: true,
      });
      setRestoreAndPublishVersionId(null);
      setActiveDraftId(null);
      onPublishSuccess();
    } catch (error) {
      setRestoreAndPublishVersionId(null);
      actions.toast.showError(toErrorMessage(error));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [restoreAndPublishVersionId, onPublishSuccess, actions]);

  const handleSaveEffectiveDate = useCallback(
    async (id: number) => {
      const dateStr = effectiveDateEdits[String(id)];
      if (!dateStr) {
        actions.toast.showError('Please choose an effective date.');
        return;
      }
      try {
        await updateEffectiveDate({
          variables: { id, effectiveDate: dateStr },
          refetchQueries: [draftsRefetch, historyRefetch],
          awaitRefetchQueries: true,
        });
        setEffectiveDateEdits((prev) => {
          const next = { ...prev };
          delete next[String(id)];
          return next;
        });
        actions.toast.showInfo('Effective date saved.');
      } catch (error) {
        actions.toast.showError(toErrorMessage(error));
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [effectiveDateEdits, actions]
  );

  const openHistoryEvents = useCallback(
    (legalPageId: number) => {
      setHistoryEventsId(legalPageId);
      loadHistoryEvents({ variables: { legalPageId } });
    },
    [loadHistoryEvents]
  );

  return (
    <Box display="flex" flexDirection="column" gap={300}>
      {rejected && (
        <Alert
          color="danger"
          title="Rejected"
          message={`Rejected${
            latestRejectionEvent?.createdAt
              ? ` on ${new Date(latestRejectionEvent.createdAt).toLocaleDateString()}`
              : ''
          }. Reason: ${latestRejectionEvent?.reason ?? 'n/a'}. Edit and resubmit when ready.`}
        />
      )}

      <Box display="flex" gap={300}>
        {/* Drafts list */}
        <Box style={{ width: 240, flexShrink: 0 }} display="flex" flexDirection="column" gap={100}>
          <Text variant="bodyStrong">Drafts</Text>
          {drafts.length === 0 && (
            <Text variant="caption" color="fgSubtle">
              No drafts yet.
            </Text>
          )}
          {drafts.map((draft) => {
            const isActive = draft.id === activeDraftId;
            return (
              <Box
                key={String(draft.id)}
                data-testid={`draft-item-${draft.id}`}
                padding={100}
                borderRadius={100}
                display="flex"
                flexDirection="column"
                gap={100}
                style={{
                  border: `1px solid ${
                    isActive
                      ? 'var(--pantry-color-borderPrimaryStrong)'
                      : 'var(--pantry-color-borderDefault)'
                  }`,
                  cursor: 'pointer',
                }}
                onClick={() => setActiveDraftId(draft.id)}
              >
                <Box display="flex" justifyContent="spaceBetween" alignItems="center" gap={100}>
                  <Tag label={draft.state} color={stateTagColor(draft.state)} size="sm" />
                  <Button
                    label="History"
                    variant="outlined"
                    color="secondary"
                    size="compact"
                    data-testid={`draft-history-btn-${draft.id}`}
                    onClick={(event) => {
                      event.stopPropagation();
                      openHistoryEvents(draft.id);
                    }}
                  />
                </Box>
                {draft.state === 'PENDING_REVIEW' && (
                  <Text variant="caption" color="fgSubtle">
                    In review — read-only
                  </Text>
                )}
              </Box>
            );
          })}
          <Box style={{ marginTop: 8 }}>
            <Button
              label="+ New draft"
              variant="outlined"
              color="secondary"
              size="compact"
              data-testid="new-draft-link"
              onClick={() => setActiveDraftId(null)}
            />
          </Box>
        </Box>

        {/* Editor */}
        <Box style={{ flex: 1 }} display="flex" flexDirection="column" gap={100}>
          <Text variant="bodyStrong">Editor</Text>
          <div
            className="LegalEditor-surface"
            style={{ height: PANE_HEIGHT }}
            onClick={() => editor?.commands.focus()}
            role="presentation"
          >
            {editor && (
              <>
                <EditorContentMenuBar editor={editor} />
                <div
                  ref={editorScrollRef}
                  className="LegalEditor-scroll"
                  onScroll={handleEditorPaneScroll}
                >
                  <EditorContent editor={editor} data-testid="legal-editor-content" />
                </div>
              </>
            )}
          </div>
          {staleApproved && (
            <Alert
              color="warning"
              message="The live page changed since this draft was approved. Re-review it before publishing (Edit to move it back to draft, then resubmit)."
              data-testid="editor-stale-baseline"
            />
          )}
          <Box display="flex" alignItems="center" gap={100}>
            {activeDraft?.state === 'APPROVED' && (
              <Button
                label="Publish"
                color="primary"
                size="compact"
                data-testid="publish-approved-btn"
                disabled={staleApproved}
                onClick={() => setPublishConfirmOpen(true)}
              />
            )}
            {activeDraft?.state === 'PENDING_REVIEW' && (
              <>
                <Text variant="caption" color="fgSubtle">
                  This page is in review and cannot be edited.
                </Text>
                <Button
                  label="Withdraw"
                  variant="outlined"
                  color="secondary"
                  size="compact"
                  data-testid="withdraw-review-btn"
                  onClick={() => setWithdrawConfirmOpen(true)}
                />
              </>
            )}
            {(activeDraftId === null || activeDraft?.state === 'DRAFT') && (
              <>
                <Button
                  label="Save draft"
                  variant="outlined"
                  color="secondary"
                  size="compact"
                  data-testid="save-draft-btn"
                  onClick={handleSave}
                />
                <Button
                  label="Submit for review"
                  color="primary"
                  size="compact"
                  data-testid="submit-btn"
                  disabled={!editor || editor.isEmpty}
                  onClick={handleSubmitForReview}
                />
                {activeDraft?.state === 'DRAFT' && (
                  <Button
                    label="Discard draft"
                    variant="outlined"
                    color="danger"
                    size="compact"
                    data-testid="discard-draft-btn"
                    onClick={() => setDiscardConfirmOpen(true)}
                  />
                )}
              </>
            )}
          </Box>
        </Box>

        {/* Preview */}
        <Box style={{ flex: 1 }} display="flex" flexDirection="column" gap={100}>
          <Text variant="bodyStrong">Preview</Text>
          <div
            ref={previewScrollRef}
            className="LegalEditor-preview"
            style={{ height: PANE_HEIGHT }}
            onScroll={handlePreviewPaneScroll}
            data-testid="legal-editor-preview"
          >
            <div
              className="Legal"
              dangerouslySetInnerHTML={{ __html: sanitizeLegalHtml(previewHtml) }}
            />
          </div>
        </Box>
      </Box>

      {/* Published history */}
      <Box display="flex" flexDirection="column" gap={100}>
        <Text variant="bodyStrong">Published history</Text>
        {history.length === 0 && (
          <Text variant="caption" color="fgSubtle">
            No published versions yet.
          </Text>
        )}
        {history.map((version) => (
          <Box
            key={String(version.id)}
            data-testid={`history-row-${version.id}`}
            display="flex"
            alignItems="center"
            gap={200}
            padding={[100, 0]}
            style={{ borderBottom: '1px solid var(--pantry-color-borderDefault)' }}
          >
            {version.state === 'PUBLISHED' ? (
              <Box display="flex" alignItems="center" gap={100} style={{ minWidth: 100 }}>
                <DateInput
                  id={`effective-date-input-${version.id}`}
                  label="Effective date"
                  data-testid={`effective-date-input-${version.id}`}
                  value={
                    effectiveDateEdits[String(version.id)] ??
                    toCentralDateInputValue(version.effectiveDate)
                  }
                  onChange={(date) =>
                    setEffectiveDateEdits((prev) => ({
                      ...prev,
                      [String(version.id)]: date ?? '',
                    }))
                  }
                />
                {/* Offset by the DateInput label (space-200) + column gap (space-50) so the
                    button centers on the input field, not the taller labeled column. */}
                <Box style={{ marginTop: 'var(--pantry-space-250)' }}>
                  <Button
                    label="Save effective date"
                    variant="outlined"
                    color="secondary"
                    size="compact"
                    data-testid={`save-effective-date-btn-${version.id}`}
                    onClick={() => handleSaveEffectiveDate(version.id)}
                  />
                </Box>
              </Box>
            ) : (
              <Text variant="body" style={{ minWidth: 100 }}>
                {formatCentralDate(version.effectiveDate)}
              </Text>
            )}
            {/* On PUBLISHED rows the DateInput is taller than the other controls because of its
                label; offset the rest of the row by that label + gap height (space-250) so the
                status chip and action buttons center on the date field instead of sitting high. */}
            <Box
              display="flex"
              alignItems="center"
              gap={200}
              style={{
                flex: 1,
                marginTop: version.state === 'PUBLISHED' ? 'var(--pantry-space-250)' : undefined,
              }}
            >
              <Box style={{ flex: 1 }}>
                <Tag label={version.state} color={stateTagColor(version.state)} size="sm" />
              </Box>
              {version.restoredFromVersionId != null && (
                <Tag
                  label={`Restore of v${version.restoredFromVersionId}`}
                  color="secondary"
                  size="sm"
                />
              )}
              <Button
                label={version.state === 'PUBLISHED' ? 'Edit' : 'Restore'}
                variant="outlined"
                color="secondary"
                size="compact"
                data-testid={`restore-btn-${version.id}`}
                onClick={() => handleRestore(version.id)}
              />
              {version.state === 'ARCHIVED' && (
                <Button
                  label="Restore & publish"
                  color="primary"
                  size="compact"
                  data-testid={`restore-and-publish-btn-${version.id}`}
                  onClick={() => setRestoreAndPublishVersionId(version.id)}
                />
              )}
            </Box>
          </Box>
        ))}
      </Box>

      {publishConfirmOpen && (
        <ConfirmationModal
          id="legal-publish-confirm"
          title="Publish legal page?"
          message={
            isNoOpPublish
              ? `This draft is identical to the currently published version. Publishing will re-archive the live page with no content change. Publish anyway?`
              : `Publish the ${typeLabel(type)} page for ${APP_LABELS[app]} (${locale}). It will go live immediately.`
          }
          labels={{ confirm: 'Publish', cancel: 'Cancel' }}
          open
          onConfirm={handlePublishApproved}
          onCancel={() => setPublishConfirmOpen(false)}
          onClose={() => setPublishConfirmOpen(false)}
        />
      )}

      {withdrawConfirmOpen && (
        <ConfirmationModal
          id="legal-withdraw-confirm"
          title="Withdraw from review?"
          message="This moves the page back to draft so you can edit and resubmit it. The current review will be canceled."
          labels={{ confirm: 'Withdraw', cancel: 'Cancel' }}
          open
          onConfirm={handleWithdrawReview}
          onCancel={() => setWithdrawConfirmOpen(false)}
          onClose={() => setWithdrawConfirmOpen(false)}
        />
      )}

      {discardConfirmOpen && (
        <ConfirmationModal
          id="legal-discard-confirm"
          title="Discard this draft?"
          message="This removes the draft from your list. It cannot be edited or submitted afterward."
          labels={{ confirm: 'Discard', cancel: 'Cancel' }}
          open
          onConfirm={handleDiscardDraft}
          onCancel={() => setDiscardConfirmOpen(false)}
          onClose={() => setDiscardConfirmOpen(false)}
        />
      )}

      {restoreAndPublishVersionId !== null && (
        <ConfirmationModal
          id="legal-restore-and-publish-confirm"
          title="Restore & publish now?"
          message="This will publish immediately and replace the live page. Review is skipped because the content is restored unchanged."
          labels={{ confirm: 'Restore & publish', cancel: 'Cancel' }}
          open
          onConfirm={handleRestoreAndPublish}
          onCancel={() => setRestoreAndPublishVersionId(null)}
          onClose={() => setRestoreAndPublishVersionId(null)}
        />
      )}

      <Modal
        id="legal-history-events"
        open={historyEventsId !== null}
        onClose={() => setHistoryEventsId(null)}
        size="lg"
      >
        <Modal.Header title="Review history" border />
        <Modal.Body>
          <Box display="flex" flexDirection="column" gap={300}>
            {historyEventsLoading && (
              <Text variant="body" color="fgSubtle">
                Loading…
              </Text>
            )}
            {!historyEventsLoading && historyEvents.length === 0 && (
              <Text variant="body" color="fgSubtle">
                No review events recorded.
              </Text>
            )}
            {!historyEventsLoading &&
              [...historyEvents]
                .sort((a, b) => (b.createdAt ?? '').localeCompare(a.createdAt ?? ''))
                .map((event) => {
                  // Regenerate the snapshot HTML from the stored JSON snapshot.
                  const snapshotHtml = renderLegalContentHtml(event.contentSnapshot) ?? null;
                  return (
                    <Box
                      key={String(event.id)}
                      display="flex"
                      flexDirection="column"
                      gap={100}
                      padding={200}
                      borderRadius={100}
                      style={{ border: '1px solid var(--pantry-color-borderDefault)' }}
                    >
                      <Box display="flex" gap={200} alignItems="center">
                        <Tag label={event.eventType} color="primary" size="lg" />
                        <Text variant="body" color="fgSubtle">
                          {event.createdAt ? new Date(event.createdAt).toLocaleString() : ''}
                        </Text>
                      </Box>
                      {event.reason && (
                        <Text variant="body">
                          <strong>Reason:</strong> {event.reason}
                        </Text>
                      )}
                      {snapshotHtml && (
                        <details>
                          <summary>
                            <Text variant="bodyStrong">View content</Text>
                          </summary>
                          <div
                            className="Legal"
                            dangerouslySetInnerHTML={{
                              __html: sanitizeLegalHtml(snapshotHtml),
                            }}
                          />
                        </details>
                      )}
                    </Box>
                  );
                })}
          </Box>
        </Modal.Body>
      </Modal>
    </Box>
  );
};

export default LegalEditor;
